/**
 * Pipeline Console Redux Reducer
 */

import { PIPELINE_ACTIONS } from '../actions/pipelineActions';

interface PipelineState {
  // Accounts
  accounts: any[]
  accountsTotal: number
  accountsLoading: boolean
  accountsError: any | null
  currentUserEmail: string | null
  
  // Current account detail
  currentAccount: any | null
  currentAccountLoading: boolean
  currentAccountError: any | null
  
  // Contacts
  contacts: any[]
  contactsTotal: number
  contactsLoading: boolean
  
  // Merged people (contacts + PayHelm team) for Company tab
  people: any[]
  peopleLoading: boolean
  
  // Threads
  threads: any[]
  threadsTotal: number
  threadsLoading: boolean
  
  // Messages (keyed by thread_id)
  messagesByThread: { [threadId: string]: any[] }
  messagesLoading: boolean
  
  // Events
  events: any[]
  eventsTotal: number
  eventsLoading: boolean
  
  // Operator State
  operatorState: any | null
  operatorStateLoading: boolean
  operatorStateSaving: boolean
  
  // Notes
  notes: any[]
  notesTotal: number
  notesSaving: boolean
  
  // Activities
  activities: any[]
  activitiesTotal: number
  activitiesLoading: boolean
  activitiesCreating: boolean
  
  // Opportunities
  opportunities: any[]
  opportunitiesLoading: boolean
  opportunitiesError: any | null
  
  // Summary
  latestSummary: any | null
  
  // Health
  health: any | null
  healthLoading: boolean
  healthError: string | null
  
  // Filters & Sort
  filters: {
    signal?: string
    stage?: string
    staleDays?: number
    searchTerm?: string
  }
  sort: {
    field: string
    direction: 'asc' | 'desc'
  }

  // Environment (staging/prod)
  environment: 'staging' | 'prod'
  envInfo: {
    allowed: string[]
    isAdmin: boolean
    context: { app: string; db: string; dbName: string; solr: string }
  } | null

  // UI State
  selectedThreadId: string | null
  activeTab: string

  // Advisor (Sales Co-Pilot)
  advisorLatest: any | null
  advisorHistory: any[]
  advisorLoading: boolean
  advisorGenerating: boolean
  advisorError: string | null

  // Advisor Chat
  advisorChatMessages: any[]
  advisorChatSending: boolean
  advisorChatError: string | null
  advisorChatConversations: any[]
  advisorActiveConversationId: string | null
}

const initialState: PipelineState = {
  // Accounts
  accounts: [],
  accountsTotal: 0,
  accountsLoading: false,
  accountsError: null,
  currentUserEmail: null,
  
  // Current account
  currentAccount: null,
  currentAccountLoading: false,
  currentAccountError: null,
  
  // Contacts
  contacts: [],
  contactsTotal: 0,
  contactsLoading: false,
  
  // Threads
  threads: [],
  threadsTotal: 0,
  threadsLoading: false,
  
  // Messages
  messagesByThread: {},
  messagesLoading: false,
  
  // Events
  events: [],
  eventsTotal: 0,
  eventsLoading: false,
  
  // Operator State
  operatorState: null,
  operatorStateLoading: false,
  operatorStateSaving: false,
  
  // Notes
  notes: [],
  notesTotal: 0,
  notesSaving: false,
  
  // Activities
  activities: [],
  activitiesTotal: 0,
  activitiesLoading: false,
  activitiesCreating: false,
  
  // Opportunities
  opportunities: [],
  opportunitiesLoading: false,
  opportunitiesError: null,
  
  // Summary
  latestSummary: null,
  
  // Health
  health: null,
  healthLoading: false,
  healthError: null,
  
  // Filters & Sort
  filters: {},
  sort: {
    field: 'monthly_sales_usd',
    direction: 'desc',
  },

  // Environment
  environment: 'staging',
  envInfo: null,

  // UI State
  selectedThreadId: null,
  activeTab: 'overview',

  // Advisor
  advisorLatest: null,
  advisorHistory: [],
  advisorLoading: false,
  advisorGenerating: false,
  advisorError: null,

  // Advisor Chat
  advisorChatMessages: [],
  advisorChatSending: false,
  advisorChatError: null,
  advisorChatConversations: [],
  advisorActiveConversationId: null,
}

export default function pipelineReducer(state = initialState, action: any): PipelineState {
  switch (action.type) {
    // Accounts
    case PIPELINE_ACTIONS.FETCH_ACCOUNTS_PENDING:
      return {
        ...state,
        accountsLoading: true,
        accountsError: null,
      }
    case PIPELINE_ACTIONS.FETCH_ACCOUNTS_FULFILLED:
      return {
        ...state,
        accounts: action.payload.accounts,
        accountsTotal: action.payload.total,
        accountsLoading: false,
        currentUserEmail: action.payload._access?.currentUserEmail || state.currentUserEmail,
      }
    case PIPELINE_ACTIONS.FETCH_ACCOUNTS_REJECTED:
      return {
        ...state,
        accountsLoading: false,
        accountsError: action.payload,
      }
    
    // Account Detail
    case PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_PENDING:
      return {
        ...state,
        currentAccount: null,
        currentAccountLoading: true,
        currentAccountError: null,
      }
    case PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_FULFILLED:
      return {
        ...state,
        currentAccount: action.payload,
        currentAccountLoading: false,
        operatorState: action.payload.operator_state,
        latestSummary: action.payload.latest_summary,
      }
    case PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_REJECTED:
      return {
        ...state,
        currentAccountLoading: false,
        currentAccountError: action.payload,
      }
    
    // Contacts
    case PIPELINE_ACTIONS.FETCH_CONTACTS_PENDING:
      return {
        ...state,
        contactsLoading: true,
      }
    case PIPELINE_ACTIONS.FETCH_CONTACTS_FULFILLED:
      return {
        ...state,
        contacts: action.payload.contacts,
        contactsTotal: action.payload.total,
        contactsLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_CONTACTS_REJECTED:
      return {
        ...state,
        contactsLoading: false,
      }
    
    // Merged people
    case PIPELINE_ACTIONS.FETCH_PEOPLE_PENDING:
      return { ...state, peopleLoading: true }
    case PIPELINE_ACTIONS.FETCH_PEOPLE_FULFILLED:
      return {
        ...state,
        people: action.payload.people || [],
        peopleLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_PEOPLE_REJECTED:
      return { ...state, peopleLoading: false }
    
    // Threads
    case PIPELINE_ACTIONS.FETCH_THREADS_PENDING:
      return {
        ...state,
        threadsLoading: true,
      }
    case PIPELINE_ACTIONS.FETCH_THREADS_FULFILLED:
      return {
        ...state,
        threads: action.payload.threads,
        threadsTotal: action.payload.total,
        threadsLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_THREADS_REJECTED:
      return {
        ...state,
        threadsLoading: false,
      }
    
    // Messages
    case PIPELINE_ACTIONS.FETCH_MESSAGES_PENDING:
      return {
        ...state,
        messagesLoading: true,
      }
    case PIPELINE_ACTIONS.FETCH_MESSAGES_FULFILLED:
      return {
        ...state,
        messagesByThread: {
          ...state.messagesByThread,
          [action.payload.threadId]: action.payload.messages,
        },
        messagesLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_MESSAGES_REJECTED:
      return {
        ...state,
        messagesLoading: false,
      }
    
    // Events
    case PIPELINE_ACTIONS.FETCH_EVENTS_PENDING:
      return {
        ...state,
        eventsLoading: true,
      }
    case PIPELINE_ACTIONS.FETCH_EVENTS_FULFILLED:
      return {
        ...state,
        events: action.payload.events,
        eventsTotal: action.payload.total,
        eventsLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_EVENTS_REJECTED:
      return {
        ...state,
        eventsLoading: false,
      }
    
    // Operator State
    case PIPELINE_ACTIONS.FETCH_OPERATOR_STATE_FULFILLED:
      return {
        ...state,
        operatorState: action.payload,
      }
    case PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_PENDING:
      return {
        ...state,
        operatorStateSaving: true,
      }
    case PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_FULFILLED:
      return {
        ...state,
        operatorState: action.payload,
        operatorStateSaving: false,
      }
    case PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_REJECTED:
      return {
        ...state,
        operatorStateSaving: false,
      }
    
    // Notes
    case PIPELINE_ACTIONS.FETCH_NOTES_FULFILLED:
      return {
        ...state,
        notes: action.payload.notes,
        notesTotal: action.payload.total,
      }
    case PIPELINE_ACTIONS.ADD_NOTE_PENDING:
      return {
        ...state,
        notesSaving: true,
      }
    case PIPELINE_ACTIONS.ADD_NOTE_FULFILLED:
      return {
        ...state,
        notes: [action.payload, ...state.notes],
        notesTotal: state.notesTotal + 1,
        notesSaving: false,
      }
    case PIPELINE_ACTIONS.ADD_NOTE_REJECTED:
      return {
        ...state,
        notesSaving: false,
      }
    
    // Activities
    case PIPELINE_ACTIONS.FETCH_ACTIVITIES_PENDING:
      return {
        ...state,
        activitiesLoading: true,
      }
    case PIPELINE_ACTIONS.FETCH_ACTIVITIES_FULFILLED:
      return {
        ...state,
        activities: action.payload.activities,
        activitiesTotal: action.payload.total,
        activitiesLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_ACTIVITIES_REJECTED:
      return {
        ...state,
        activitiesLoading: false,
      }
    case PIPELINE_ACTIONS.CREATE_ACTIVITY_PENDING:
      return {
        ...state,
        activitiesCreating: true,
      }
    case PIPELINE_ACTIONS.CREATE_ACTIVITY_FULFILLED:
      return {
        ...state,
        activities: [action.payload, ...state.activities],
        activitiesTotal: state.activitiesTotal + 1,
        activitiesCreating: false,
      }
    case PIPELINE_ACTIONS.CREATE_ACTIVITY_REJECTED:
      return {
        ...state,
        activitiesCreating: false,
      }
    
    // Opportunities
    case PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_PENDING:
      return {
        ...state,
        opportunitiesLoading: true,
        opportunitiesError: null,
      }
    case PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_FULFILLED:
      return {
        ...state,
        opportunities: action.payload,
        opportunitiesLoading: false,
      }
    case PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_REJECTED:
      return {
        ...state,
        opportunitiesLoading: false,
        opportunitiesError: action.payload,
      }
    
    // Summary
    case PIPELINE_ACTIONS.FETCH_SUMMARY_FULFILLED:
      return {
        ...state,
        latestSummary: action.payload,
      }
    
    // Health
    case PIPELINE_ACTIONS.FETCH_HEALTH_PENDING:
      return {
        ...state,
        healthLoading: true,
        healthError: null,
      }
    case PIPELINE_ACTIONS.FETCH_HEALTH_FULFILLED:
      return {
        ...state,
        health: action.payload,
        healthLoading: false,
        healthError: null,
      }
    case PIPELINE_ACTIONS.FETCH_HEALTH_REJECTED:
      return {
        ...state,
        healthLoading: false,
        healthError: action.payload?.message || 'Failed to fetch pipeline health',
      }
    
    // Filters & Sort
    case PIPELINE_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      }
    case PIPELINE_ACTIONS.SET_SORT:
      return {
        ...state,
        sort: action.payload,
      }
    case PIPELINE_ACTIONS.CLEAR_FILTERS:
      return {
        ...state,
        filters: {},
      }
    
    // UI
    case PIPELINE_ACTIONS.SET_SELECTED_THREAD:
      return {
        ...state,
        selectedThreadId: action.payload,
      }
    case PIPELINE_ACTIONS.SET_ACTIVE_TAB:
      return {
        ...state,
        activeTab: action.payload,
      }

    case PIPELINE_ACTIONS.SET_ENVIRONMENT:
      return {
        ...state,
        environment: action.payload,
      }

    case PIPELINE_ACTIONS.SET_ENV_INFO:
      return {
        ...state,
        envInfo: action.payload ? {
          allowed: action.payload.allowed,
          isAdmin: action.payload.isAdmin,
          context: action.payload.context,
        } : null,
      }

    // Advisor (Sales Co-Pilot)
    case PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_PENDING:
      return { ...state, advisorLoading: true, advisorError: null }
    case PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_FULFILLED:
      return { ...state, advisorLatest: action.payload, advisorLoading: false }
    case PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_REJECTED:
      return { ...state, advisorLoading: false, advisorError: action.payload }
    case PIPELINE_ACTIONS.GENERATE_ADVISOR_PENDING:
      return { ...state, advisorGenerating: true, advisorError: null }
    case PIPELINE_ACTIONS.GENERATE_ADVISOR_FULFILLED:
      return {
        ...state,
        advisorLatest: action.payload?.insight ?? state.advisorLatest,
        advisorGenerating: false,
        advisorError: action.payload?.success === false ? action.payload.error : null,
      }
    case PIPELINE_ACTIONS.GENERATE_ADVISOR_REJECTED:
      return { ...state, advisorGenerating: false, advisorError: action.payload }
    case PIPELINE_ACTIONS.FETCH_ADVISOR_HISTORY_FULFILLED:
      return { ...state, advisorHistory: action.payload }

    // Advisor Chat
    case PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_PENDING:
      return {
        ...state,
        advisorChatSending: true,
        advisorChatError: null,
        advisorChatMessages: [
          ...state.advisorChatMessages,
          {
            id: `pending-${Date.now()}`,
            role: 'user',
            content: action.payload.message,
            created_at: new Date().toISOString(),
          },
        ],
      }
    case PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_FULFILLED:
      return {
        ...state,
        advisorChatSending: false,
        advisorChatMessages: [...state.advisorChatMessages, action.payload],
      }
    case PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_REJECTED:
      return { ...state, advisorChatSending: false, advisorChatError: action.payload }
    case PIPELINE_ACTIONS.FETCH_ADVISOR_CHAT_HISTORY_FULFILLED:
      return { ...state, advisorChatMessages: action.payload || [] }
    case PIPELINE_ACTIONS.CLEAR_ADVISOR_CHAT_HISTORY:
      return { ...state, advisorChatMessages: [], advisorChatError: null }

    // Conversations
    case PIPELINE_ACTIONS.FETCH_CONVERSATIONS_FULFILLED:
      return { ...state, advisorChatConversations: action.payload || [] }
    case PIPELINE_ACTIONS.CREATE_CONVERSATION_FULFILLED:
      return {
        ...state,
        advisorChatConversations: [action.payload, ...state.advisorChatConversations],
      }
    case PIPELINE_ACTIONS.DELETE_CONVERSATION_FULFILLED:
      return {
        ...state,
        advisorChatConversations: state.advisorChatConversations.filter((c: any) => c.id !== action.payload),
        advisorActiveConversationId: state.advisorActiveConversationId === action.payload ? null : state.advisorActiveConversationId,
        advisorChatMessages: state.advisorActiveConversationId === action.payload ? [] : state.advisorChatMessages,
      }
    case PIPELINE_ACTIONS.SET_ACTIVE_CONVERSATION:
      return { ...state, advisorActiveConversationId: action.payload, advisorChatMessages: [], advisorChatError: null }
    case PIPELINE_ACTIONS.RENAME_CONVERSATION_FULFILLED:
      return {
        ...state,
        advisorChatConversations: state.advisorChatConversations.map((c: any) =>
          c.id === action.payload.conversationId ? { ...c, title: action.payload.title } : c
        ),
      }

    default:
      return state
  }
}

// Selectors
export const selectPipelineAccounts = (state: any) => state.pipeline?.accounts || []
export const selectPipelineAccountsTotal = (state: any) => state.pipeline?.accountsTotal || 0
export const selectPipelineAccountsLoading = (state: any) => state.pipeline?.accountsLoading || false
export const selectPipelineCurrentUserEmail = (state: any) => state.pipeline?.currentUserEmail || null

export const selectCurrentAccount = (state: any) => state.pipeline?.currentAccount || null
export const selectCurrentAccountLoading = (state: any) => state.pipeline?.currentAccountLoading || false

export const selectPipelineContacts = (state: any) => state.pipeline?.contacts || []
export const selectPipelinePeople = (state: any) => state.pipeline?.people || []
export const selectPipelinePeopleLoading = (state: any) => state.pipeline?.peopleLoading || false
export const selectPipelineThreads = (state: any) => state.pipeline?.threads || []
export const selectPipelineEvents = (state: any) => state.pipeline?.events || []

export const selectOperatorState = (state: any) => state.pipeline?.operatorState || null
export const selectOperatorStateSaving = (state: any) => state.pipeline?.operatorStateSaving || false

export const selectPipelineNotes = (state: any) => state.pipeline?.notes || []

export const selectPipelineActivities = (state: any) => state.pipeline?.activities || []
export const selectPipelineActivitiesTotal = (state: any) => state.pipeline?.activitiesTotal || 0
export const selectPipelineActivitiesLoading = (state: any) => state.pipeline?.activitiesLoading || false
export const selectPipelineActivitiesCreating = (state: any) => state.pipeline?.activitiesCreating || false

export const selectOpportunities = (state: any) => state.pipeline?.opportunities || []
export const selectOpportunitiesLoading = (state: any) => state.pipeline?.opportunitiesLoading || false

export const selectLatestSummary = (state: any) => state.pipeline?.latestSummary || null

export const selectPipelineHealth = (state: any) => state.pipeline?.health || null
export const selectPipelineHealthLoading = (state: any) => state.pipeline?.healthLoading || false
export const selectPipelineHealthError = (state: any) => state.pipeline?.healthError || null

export const selectPipelineFilters = (state: any) => state.pipeline?.filters || {}
export const selectPipelineSort = (state: any) => state.pipeline?.sort || { field: 'monthly_sales_usd', direction: 'desc' }
export const selectPipelineEnvironment = (state: any) => state.pipeline?.environment || 'staging'
export const selectPipelineEnvInfo = (state: any) => state.pipeline?.envInfo || null

export const selectSelectedThreadId = (state: any) => state.pipeline?.selectedThreadId || null
export const selectActiveTab = (state: any) => state.pipeline?.activeTab || 'overview'

export const selectMessagesByThread = (state: any, threadId: string) => 
  state.pipeline?.messagesByThread?.[threadId] || []

// Advisor selectors
export const selectAdvisorLatest = (state: any) => state.pipeline?.advisorLatest || null
export const selectAdvisorHistory = (state: any) => state.pipeline?.advisorHistory || []
export const selectAdvisorLoading = (state: any) => state.pipeline?.advisorLoading || false
export const selectAdvisorGenerating = (state: any) => state.pipeline?.advisorGenerating || false
export const selectAdvisorError = (state: any) => state.pipeline?.advisorError || null

// Advisor Chat selectors
export const selectAdvisorChatMessages = (state: any) => state.pipeline?.advisorChatMessages || []
export const selectAdvisorChatSending = (state: any) => state.pipeline?.advisorChatSending || false
export const selectAdvisorChatError = (state: any) => state.pipeline?.advisorChatError || null
export const selectAdvisorChatConversations = (state: any) => state.pipeline?.advisorChatConversations || []
export const selectAdvisorActiveConversationId = (state: any) => state.pipeline?.advisorActiveConversationId || null
